/**
 * AR Vision Demo — global registration of the workflow-authoring tools
 * (scenes 5, 5b + 9).
 *
 * THROWAWAY DEMO CODE. The scene-5 prompt ("create me a workflow…") is typed
 * while the user is on the AR summary page, NOT on the demo workflow canvas, so
 * the `ar_create_reminder_workflow` tool must already be registered when the
 * interceptor fires. This tiny always-mounted component (rendered inside the
 * demo wrapper, alongside ArDemoControls) registers both workflow tools
 * directly with the demo-script registry (the interceptor's source of truth) —
 * NOT via `useArAgentPage`, since that drives the singleton agent-module config
 * that per-page beats own. The registry is keyed by tool name, so this never
 * collides with a page's own tools:
 *   - `ar_create_reminder_workflow` (scene 5): flips the workflow `created`
 *     flag and navigates to the demo workflow canvas.
 *   - `ar_highlight_statement` (scene 5b): swaps the statement preview to the
 *     bold + yellow-highlighted version and updates the step wording.
 *   - `ar_amend_workflow` (scene 9): flips `amended`, revealing the
 *     overdue>1-month → dispute-call branch, and navigates back to the canvas.
 *
 * Renders nothing. Delete alongside frontend/src/demo/ar.
 */

import { useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import paths from '@/paths';
import type { FrontendToolDefinition } from '@/hooks/useFrontendTool';
import { isArDemoMode } from './demo-mode';
import { updateArDemoState } from './fixture-store';
import { registerArDemoTools } from './demo-script';
import { awaitNextWorkflowBuild } from './workflow-build-signal';
import type { DemoWorkflowEdge, DemoWorkflowNode } from './fixture-store.types';

/**
 * The scene-9 amendment: a condition node that branches off "find customers"
 * and a call node, plus the two edges wiring them in. Added to the seed graph
 * when `ar_amend_workflow` fires (idempotent — re-firing is a no-op).
 */
const AMENDMENT_NODES: DemoWorkflowNode[] = [
  {
    id: 'wf-overdue-condition',
    category: 'Control Flow',
    stepType: 'branch_if',
    title: 'Invoice overdue > 1 month?',
    subtitle: 'Branches the flow when an invoice is 30+ days past due.',
    sockets: { inputs: ['customers'], outputs: ['overdue'] },
    position: { x: 720, y: 440 },
    added: true,
  },
  {
    id: 'wf-dispute-call',
    category: 'Actions',
    stepType: 'place_call',
    title: 'Place a call to open a dispute',
    subtitle:
      'Nami calls the customer to open a dispute instead of just emailing.',
    sockets: { inputs: ['overdue'] },
    position: { x: 1030, y: 440 },
    added: true,
  },
];

const AMENDMENT_EDGES: DemoWorkflowEdge[] = [
  {
    id: 'e-find-overdue',
    source: 'wf-find-customers',
    target: 'wf-overdue-condition',
    label: 'overdue > 1 month',
    added: true,
  },
  {
    id: 'e-overdue-call',
    source: 'wf-overdue-condition',
    target: 'wf-dispute-call',
    added: true,
  },
];

/** Build a no-arg FrontendToolDefinition (no JSON schema). When `awaitable`,
 *  the interceptor waits for `execute` to resolve before streaming narration. */
function noArgTool(
  name: string,
  description: string,
  execute: () => void | Promise<void>,
  awaitable = false,
): FrontendToolDefinition {
  return {
    name,
    description,
    awaitable,
    schema: {
      name,
      description,
      parameters: z.toJSONSchema(z.object({})) as Record<string, unknown>,
      awaitable,
    },
    execute,
  };
}

export function DemoWorkflowTools() {
  const navigate = useNavigate();

  useEffect(() => {
    const tools: FrontendToolDefinition[] = [
      noArgTool(
        'ar_create_reminder_workflow',
        'Assemble and open the weekly payment-reminder workflow on the canvas.',
        async () => {
          // Arm the build signal BEFORE navigating so the canvas can resolve it
          // once the staggered reveal finishes; awaiting it defers Nami's
          // "Done…" narration until the workflow has visibly built itself.
          const built = awaitNextWorkflowBuild();
          updateArDemoState((draft) => {
            draft.workflow.created = true;
          });
          void navigate({ to: paths.authenticated.demoArWorkflow });
          await built;
        },
        true,
      ),
      noArgTool(
        'ar_amend_workflow',
        'Append the "overdue > 1 month -> call to dispute" branch to the reminder workflow.',
        async () => {
          const built = awaitNextWorkflowBuild();
          updateArDemoState((draft) => {
            const { workflow } = draft;
            workflow.created = true;
            workflow.amended = true;
            // Idempotent: only add the branch once.
            for (const node of AMENDMENT_NODES) {
              if (!workflow.nodes.some((n) => n.id === node.id)) {
                workflow.nodes.push(node);
              }
            }
            for (const edge of AMENDMENT_EDGES) {
              if (!workflow.edges.some((e) => e.id === edge.id)) {
                workflow.edges.push(edge);
              }
            }
            if (
              !workflow.steps.some((s) => s.includes('overdue by more than'))
            ) {
              workflow.steps.push(
                'If an invoice is overdue by more than a month, place a call to open a dispute',
              );
            }
          });
          void navigate({ to: paths.authenticated.demoArWorkflow });
          await built;
        },
        true,
      ),
      noArgTool(
        'ar_highlight_statement',
        'Restyle the statement PDF so overdue invoices are bold and highlighted yellow.',
        () => {
          updateArDemoState((draft) => {
            const { workflow } = draft;
            workflow.created = true;
            workflow.statementHighlighted = true;
            // Reflect the new styling in the document step + description.
            const statementNode = workflow.nodes.find(
              (n) => n.id === 'wf-statement',
            );
            if (statementNode) {
              statementNode.subtitle =
                'Builds each customer’s statement PDF — overdue invoices bolded and highlighted yellow.';
            }
            workflow.steps = workflow.steps.map((s) =>
              s === 'Generate statement PDF'
                ? 'Generate statement PDF (overdue invoices bold + highlighted yellow)'
                : s,
            );
            workflow.description =
              'Every week, email each customer with unpaid invoices a statement of their outstanding invoices. Overdue invoices are bolded and highlighted yellow in the PDF.';
          });
          void navigate({ to: paths.authenticated.demoArWorkflow });
        },
      ),
    ];
    registerArDemoTools('ar-demo-workflow-tools', tools);
  }, [navigate]);

  return null;
}

/** Mount guard so the tools register only in demo mode. */
export function DemoWorkflowToolsMount() {
  if (!isArDemoMode()) return null;
  return <DemoWorkflowTools />;
}
